import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Ticket, Calendar, Film, Download, ExternalLink } from 'lucide-react';
import { fetchMyPurchases, formatMoney, friendlyError } from '@/lib/api';
import { DASHBOARD } from '@/constants/testIds';
import { toast } from 'sonner';
import { SectionTitle } from '../SectionTitle';
import { EmptyState } from '../EmptyState';
import { STATUS_BADGE_COLORS, STATUS_LABEL } from '../AccessBadge';

const CATEGORY_ICON = {
  view: Film,
  download: Download,
  subscription: Ticket,
};

function PurchaseRow({ p }) {
  const Icon = CATEGORY_ICON[p.category] || Ticket;
  // Link al recurso
  let linkTo = null;
  let linkLabel = 'Ver';
  if (p.category === 'view') {
    if (p.source_type === 'content' && p.event?.slug) {
      // Contenido individual (PPC): abrir exactamente el contenido comprado.
      const vid = p.video_id || p.video?.id;
      linkTo = vid
        ? `/eventos/${p.event.slug}/watch?v=${vid}`
        : `/eventos/${p.event.slug}/watch`;
      linkLabel = 'Reproducir';
    } else if (p.event?.slug) {
      linkTo = `/eventos/${p.event.slug}`;
      linkLabel = 'Ver partido';
    }
  } else if (p.category === 'download' && p.event?.slug) {
    linkTo = `/eventos/${p.event.slug}`;
    linkLabel = 'Ver partido';
  }

  return (
    <li
      data-testid={DASHBOARD.purchaseRow(p.id)}
      className="flex items-center gap-3 px-3 py-3 rounded-xl border border-[var(--ns-border)] hover:bg-[var(--ns-bg-2)]/40 transition-colors"
    >
      <div className="h-9 w-9 rounded-lg bg-[var(--ns-bg-2)] text-[var(--ns-gold-700)] inline-flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{p.title}</p>
        <p className="text-xs text-[var(--ns-text-muted)] flex items-center gap-1 flex-wrap">
          <Calendar className="h-3 w-3" />
          {p.created_at ? new Date(p.created_at).toLocaleDateString('es-ES') : '—'}
          <span className="mx-1">·</span>
          {formatMoney(p.amount_cents, p.currency)}
        </p>
      </div>
      <Badge className={`text-[10px] uppercase ${STATUS_BADGE_COLORS[p.status] || ''}`}>
        {STATUS_LABEL[p.status] || p.status}
      </Badge>
      {linkTo ? (
        <Button asChild variant="ghost" size="sm" className="text-xs text-[var(--ns-gold-700)] hover:bg-[var(--ns-amber-50)]">
          <Link to={linkTo} className="inline-flex items-center gap-1">
            {linkLabel}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </Button>
      ) : null}
    </li>
  );
}

export function MyPurchasesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchMyPurchases();
        if (alive) setItems(data.items || []);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Card className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]">
      <SectionTitle
        icon={Ticket}
        label="Mis compras"
        hint="Historial unificado de partidos, contenidos y descargas."
      />
      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-xl" />
        ))}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Ticket}
          title="Sin compras todavía"
          description="Cuando compres un partido, contenido o descarga aparecerá aquí."
          ctaLabel="Explorar partidos"
          ctaTo="/eventos"
          testId={DASHBOARD.purchasesEmpty}
        />
      ) : (
        <ul className="space-y-2">
          {items.map((p) => <PurchaseRow key={p.id} p={p} />)}
        </ul>
      )}
    </Card>
  );
}
